import { useCallback, useMemo } from 'react';
import { useLayoutPreferences } from './use-layout-preferences';
import { Filter } from '@/types/filters';

export interface SavedView {
  id: string;
  name: string;
  filters: Filter[];
  createdAt: string;
  updatedAt: string;
}

export function useSavedViews(viewType: string = 'pulls_saved_views') {
  const { getLayoutPreference, setLayoutPreference, deleteLayoutPreference, preferences, isLoading } = useLayoutPreferences(viewType);
  
  // Get all saved views
  const savedViews = useMemo(() => {
    if (!preferences) return [];
    
    // Each preference is a saved view
    return preferences.map(pref => {
      try {
        const viewData = JSON.parse(pref.layoutValue);
        return {
          id: pref.layoutKey,
          ...viewData
        } as SavedView;
      } catch (error) {
        console.error('Failed to parse saved view:', pref.layoutKey, error);
        return null;
      }
    }).filter(Boolean) as SavedView[];
  }, [preferences]);
  
  // Save a new view or update existing
  const saveView = useCallback(async (name: string, filters: Filter[]) => {
    const now = new Date().toISOString();
    const viewId = name.toLowerCase().replace(/\s+/g, '-');
    
    const viewData: Omit<SavedView, 'id'> = {
      name,
      filters,
      createdAt: savedViews.find(v => v.id === viewId)?.createdAt || now,
      updatedAt: now,
    };
    
    await setLayoutPreference(viewId, JSON.stringify(viewData));
  }, [setLayoutPreference, savedViews]);
  
  // Load a saved view (returns the filters)
  const loadView = useCallback((viewId: string): Filter[] | null => {
    const view = savedViews.find(v => v.id === viewId);
    return view?.filters || null;
  }, [savedViews]);
  
  // Delete a saved view
  const deleteView = useCallback(async (viewId: string) => {
    await deleteLayoutPreference(viewId);
  }, [deleteLayoutPreference]);
  
  // Get a specific view by ID
  const getView = useCallback((viewId: string): SavedView | undefined => {
    return savedViews.find(v => v.id === viewId);
  }, [savedViews]);
  
  // Touch a view to update its timestamp (mark as recently used)
  const touchView = useCallback(async (viewId: string) => {
    const view = savedViews.find(v => v.id === viewId);
    if (!view) return;
    
    try {
      const viewData = JSON.parse(getLayoutPreference(viewId) || '{}');
      // Update only the timestamp, not the filters
      await setLayoutPreference(viewId, JSON.stringify({
        ...viewData,
        updatedAt: new Date().toISOString(),
      }));
    } catch (error) {
      console.error('Failed to touch view:', viewId, error);
    }
  }, [savedViews, getLayoutPreference, setLayoutPreference]);
  
  return {
    savedViews,
    saveView,
    loadView,
    deleteView,
    getView,
    touchView,
    isLoading,
  };
}